import React from "react";
import { ChatHeader, ChatInput, ChatMessagesList } from "./components";
import { useAuth, useChat } from "./hooks";
import "./App.css";

export default function App() {
  const {
    userName,
    userFullName,
    isSignedIn,
    isAuthenticating,
    authError,
    getAccessToken,
  } = useAuth();

  // const userName = "Jone Doe";
  // const userFullName = "Johnathan Doe";
  // const isSignedIn = true;
  // const isAuthenticating = false;
  // const authError = null;
  // const getAccessToken = async () => "dummy_token";

  const { messages, sendMessage, isWaitingForResponse } = useChat(
    getAccessToken,
    userFullName,
  );
  const [inputText, setInputText] = React.useState("");
  const [showCommandMenu, setShowCommandMenu] = React.useState(false);
  const [selectedCommandIndex, setSelectedCommandIndex] = React.useState(0);

  // Define available commands based on environment
  const isDevelopment = import.meta.env.VITE_MODE !== "production";
  const availableCommands = isDevelopment
    ? [
        { name: "useemail", description: "Use email command" },
        { name: "onepager", description: "Generate one pager" },
      ]
    : [{ name: "onepager", description: "Generate one pager" }];

  // Handle input change and detect "/" command
  const handleInputChange = (value: string) => {
    setInputText(value);
    const shouldShow = value === "/";
    setShowCommandMenu(shouldShow);
    if (shouldShow) {
      setSelectedCommandIndex(0);
    }
  };

  // Handle command selection
  const handleCommandSelect = (command: string) => {
    setInputText(`/${command} `);
    setShowCommandMenu(false);
    setSelectedCommandIndex(0);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showCommandMenu) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedCommandIndex((prev) =>
        prev < availableCommands.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedCommandIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && showCommandMenu) {
      e.preventDefault();
      handleCommandSelect(availableCommands[selectedCommandIndex].name);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setShowCommandMenu(false);
      setSelectedCommandIndex(0);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputText.trim() === "" || isWaitingForResponse) return;

    await sendMessage(inputText);
    setInputText("");
  };

  if (isAuthenticating) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="modern-loader">
            <div className="loader-ring"></div>
            <div className="loader-ring"></div>
            <div className="loader-ring"></div>
            <div className="loader-dot"></div>
          </div>
          <p className="loading-text">Authenticating</p>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="App">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2 className="error-title">Authentication Failed</h2>
          <p className="error-message">{authError}</p>
          <button
            className="error-retry-button"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <ChatHeader isSignedIn={isSignedIn} userName={userName} />
      <ChatMessagesList messages={messages} userName={userName} />
      {/* AI generated response notice above ChatInput */}
      <div
        style={{
          marginBottom: "8px",
          color: "#888",
          fontSize: "0.95em",
          textAlign: "center",
        }}
      >
        This response is AI generated. Refer to Policy / Universe for details.
      </div>
      <ChatInput
        inputText={inputText}
        setInputText={handleInputChange}
        onSubmit={handleSendMessage}
        isSignedIn={isSignedIn}
        isAuthenticating={isAuthenticating}
        disabled={isWaitingForResponse}
        showCommandMenu={showCommandMenu}
        availableCommands={availableCommands}
        onCommandSelect={handleCommandSelect}
        onKeyDown={handleKeyDown}
        selectedCommandIndex={selectedCommandIndex}
      />
    </div>
  );
}
